import { useTranslation } from 'react-i18next'
import { Bell, Play } from 'lucide-react'
import * as Switch from '@radix-ui/react-switch'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { playChimePreview, playCriticalSound, prepareCriticalSound, unlockAlertSound } from '@/lib/alertSound'
import { useCriticalSoundSettings } from '@/hooks/useCriticalSoundSettings'
import { useAuthStore } from '@/store/authStore'
import { useDisplayPrefs } from '@/store/displayPrefsStore'

// ============================================================================
// Alert sounds (#294 redesign, CP 294 Section 2) — every user, saved in this browser
// ============================================================================
// The normal chime on/off and its volume (10–100 %), and the critical sound's volume (30–100 %: never lower,
// so a critical alert is never silent). A change reaches the other open tabs of this browser at once (the
// display prefs store). Nothing here waits on the server: the critical Play uses the super admin's current
// sound (Settings → System) once it is known, the default until then.

const CHIME_SWITCH_ID = 'alert-sounds-chime'
const CHIME_VOLUME_ID = 'alert-sounds-chime-volume'
const CRITICAL_VOLUME_ID = 'alert-sounds-critical-volume'

const ROW = 'flex flex-wrap items-center gap-x-3.5 gap-y-2.5 p-4'

/** A volume slider with its value in % and a Play button. */
function VolumeControl({
  id, min, value, onChange, onPlay, playLabel, describedBy,
}: {
  id: string
  min: number
  value: number
  onChange: (percent: number) => void
  onPlay: () => void
  playLabel: string
  describedBy?: string
}) {
  const { t } = useTranslation('settings')
  return (
    <div className="flex min-w-0 flex-[1_1_280px] items-center gap-2.5">
      <input
        id={id}
        type="range"
        min={min}
        max={100}
        step={5}
        value={value}
        aria-describedby={describedBy}
        aria-valuetext={`${value} %`}
        onChange={(e) => onChange(Number(e.target.value))}
        className="min-w-0 flex-1 accent-primary"
      />
      <output htmlFor={id} className="w-11 shrink-0 text-right text-[13px] font-semibold tabular-nums">
        {`${value} %`}
      </output>
      <Button type="button" variant="outline" size="sm" aria-label={playLabel} onClick={onPlay} className="shrink-0">
        <Play className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
        {t('alertSounds.play')}
      </Button>
    </div>
  )
}

export function AlertSoundsSection() {
  const { t } = useTranslation('settings')
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false)
  const chimeEnabled = useDisplayPrefs((s) => s.chimeEnabled)
  const chimeVolume = useDisplayPrefs((s) => s.chimeVolume)
  const criticalVolume = useDisplayPrefs((s) => s.criticalVolume)
  const setChimeEnabled = useDisplayPrefs((s) => s.setChimeEnabled)
  const setChimeVolume = useDisplayPrefs((s) => s.setChimeVolume)
  const setCriticalVolume = useDisplayPrefs((s) => s.setCriticalVolume)
  // The defaults while loading, on error and on an old API: this section never waits for it.
  const { sound } = useCriticalSoundSettings()

  // A click is a gesture: unlock the sound first, then play. The chime's Play is a test, so it plays even
  // with the live chime switched off.
  const playChime = () => { void unlockAlertSound().then(() => playChimePreview(chimeVolume / 100)) }
  // #338: the client's recording is a file: wait for it to load (at once when it is cached), as Settings → System does.
  const playCritical = () => {
    void unlockAlertSound()
      .then(() => prepareCriticalSound(sound))
      .then(() => playCriticalSound(sound, criticalVolume / 100))
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{t('alertSounds.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('alertSounds.description')}</p>
      </div>

      <div className="divide-y rounded-lg border bg-card shadow-sm">
        <div className={ROW}>
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
            <Bell className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <label htmlFor={CHIME_SWITCH_ID} className="text-sm font-medium">{t('alertSounds.chime.title')}</label>
            <p id={`${CHIME_SWITCH_ID}-hint`} className="text-xs text-muted-foreground">{t('alertSounds.chime.hint')}</p>
          </div>
          <Switch.Root
            id={CHIME_SWITCH_ID}
            checked={chimeEnabled}
            onCheckedChange={setChimeEnabled}
            aria-describedby={`${CHIME_SWITCH_ID}-hint`}
            className={cn(
              'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-muted-foreground/30',
            )}
          >
            <Switch.Thumb className="pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
          </Switch.Root>
        </div>

        <div className={ROW}>
          <div className="min-w-0 flex-[1_1_200px]">
            <label htmlFor={CHIME_VOLUME_ID} className="block text-sm font-medium">{t('alertSounds.chimeVolume')}</label>
          </div>
          <VolumeControl
            id={CHIME_VOLUME_ID}
            min={10}
            value={chimeVolume}
            onChange={setChimeVolume}
            onPlay={playChime}
            playLabel={t('alertSounds.playChime')}
          />
        </div>

        <div className={ROW}>
          <div className="min-w-0 flex-[1_1_200px]">
            <label htmlFor={CRITICAL_VOLUME_ID} className="block text-sm font-medium">{t('alertSounds.criticalVolume')}</label>
            <p id={`${CRITICAL_VOLUME_ID}-hint`} className="text-xs font-semibold text-red-700 dark:text-red-300">
              {t('alertSounds.criticalVolumeHint')}
            </p>
          </div>
          <VolumeControl
            id={CRITICAL_VOLUME_ID}
            min={30}
            value={criticalVolume}
            onChange={setCriticalVolume}
            onPlay={playCritical}
            playLabel={t('alertSounds.playCritical')}
            describedBy={`${CRITICAL_VOLUME_ID}-hint`}
          />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        {isSuperuser ? t('alertSounds.setByAdminSystem') : t('alertSounds.setByAdmin')}
      </p>
    </section>
  )
}
